import { Input } from "../../../../../common/Input";
import { formatDateTime } from "../../../../../utils/textUtils";
import PersonIcon from '@mui/icons-material/Person';
import { StatusSelect } from "../formComponents/StatusSelect";
import { EmployeePickerField } from "../../../../../common/selectComponents/EmployeePicker";

export function SetupTab({
	form,
	errorState,
	handleInputChange,
	showGeneralMessage
}) {
	// Function to combine date and time into a single datetime string
	const combineDateTime = (dateStr, timeStr) => {
		if (!dateStr) return null; // Return null instead of empty string

		// Ensure timeStr is just HH:mm
		const cleanTime = timeStr && timeStr.includes('T') ? timeStr.split('T')[1].substring(0, 5) : (timeStr || "00:00");

		return `${dateStr.split('T')[0]}T${cleanTime}`;
	};

	// Function to split datetime into date and time components
	const splitDateTime = (datetimeStr) => {
		if (!datetimeStr) return { date: "", time: "" };
		const parts = datetimeStr.split("T");
		return {
			date: parts[0],
			time: parts[1] ? parts[1].substring(0, 5) : "00:00"
		};
	};

	const handleLocalInputChange = (field, value) => {
		handleInputChange(field, value);
	};

	// Handler for datetime-local inputs
	const handleDateTimeChange = (field, value) => {
		const { date, time } = splitDateTime(value);

		if (field === "appraisalStart") {
			handleInputChange('dateStart', date);
			handleInputChange('timeStart', time);
		} else if (field === "appraisalEnd") {
			handleInputChange('dateEnd', date);
			handleInputChange('timeEnd', time);
		}
	};

	const handleSelectEmployee = (employee) => {
		handleInputChange('employeePersonalInformationId', employee.id);
		handleInputChange('employeeData', {
			id: employee.id,
			fullName: employee.fullName,
			employeeId: employee.employeeId,
			dateHired: employee.employeeInformationDateEmployed,
			department: employee.employeeInformationDepartmentDescription,
			position: employee.employeeInformationPositionDescription
		});
	};

	const handleSelectRater = (employee) => {
		handleInputChange('raterEmployeePersonalInformationId', employee.id);
		handleInputChange('raterData', {
			id: employee.id,
			fullName: employee.fullName,
			position: employee.employeeInformationPositionDescription
		});
	};

	const clearEmployee = () => {
		handleInputChange('employeePersonalInformationId', "");
		handleInputChange('employeeData', null);
	};

	const clearRater = () => {
		handleInputChange('raterEmployeePersonalInformationId', "");
		handleInputChange('raterData', null);
	};

	// Get combined datetime values for the inputs
	const appraisalStart = combineDateTime(form.dateStart, form.timeStart);
	const appraisalEnd = combineDateTime(form.dateEnd, form.timeEnd);

	// console.log("Error states:", errorState);

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* Employee Information Section */}
				<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
					<div className="py-1 border-b border-gray-300">
						<h3 className="font-medium text-gray-700">Employee Information</h3>
					</div>
					<div className="grid grid-cols-1 gap-4 pb-3 pt-3">

						{/* Employee Selection */}
						<EmployeePickerField
							label="Employee:"
							isRequired={true}
							employeePersonalInformationId={form.employeePersonalInformationId}
							displayValue={form.employeeData
								? `${form.employeeData.employeeId} - ${form.employeeData.fullName}`
								: ""}
							onSelect={handleSelectEmployee}
							onClear={clearEmployee}
							error={errorState.employee && !form.employeeData}
							errorMessage="Please select an employee"
							allowClear={false}
						/>

						{/* Selected Employee Details */}
						<div className="col-span-1">
							<div className="space-y-2">

								{form.employeeData ? (
									<>
										<label className="block text-sm font-semibold text-gray-500">
											Employee Details
										</label>
										<div className="bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-sm">
											<div className="flex items-start space-x-3">
												{/* Details */}
												<div className="flex-1 min-w-0">

													<div className="">
														<div className="flex items-center space-x-2 mb-1">
															<PersonIcon className="w-4 h-4 text-gray-400" />
															<h3 className="text-sm font-semibold text-gray-500 truncate">
																{form.employeeData.employeeId || 'N/A'} - {form.employeeData.fullName || 'Unnamed Employee'}
															</h3>
														</div>
													</div>

													{/* Quick Stats Grid */}
													<div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
														<div className="space-y-0.5">
															<span className="text-gray-400">Department</span>
															<p className="font-medium text-gray-700 truncate">
																{form.employeeData.department || 'N/A'}
															</p>
														</div>
														<div className="space-y-0.5">
															<span className="text-gray-400">Position</span>
															<p className="font-medium text-gray-700">
																{form.employeeData.position || 'N/A'}
															</p>
														</div>
													</div>
												</div>
											</div>
										</div>
									</>
								) : (
									<div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-6 text-center">
										<div className="mb-3">
											<div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
												<PersonIcon className="text-gray-400" />
											</div>
										</div>
										<p className="text-sm font-medium text-gray-600 mb-1">No Employee Selected</p>
										<p className="text-xs text-gray-400">Search for an employee to continue</p>
									</div>
								)}
							</div>
						</div>

					</div>
				</div>

				{/* Rater Information Section */}
				<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
					<div className="py-1 border-b border-gray-300">
						<h3 className="font-medium text-gray-700">Rater Information</h3>
					</div>
					<div className="grid grid-cols-1 gap-4 pb-3 pt-3">

						{/* Rater Selection */}
						<EmployeePickerField
							label="Rater:"
							isRequired={true}
							employeePersonalInformationId={form.raterEmployeePersonalInformationId}
							displayValue={form.raterData ? `${form.raterData.fullName}` : ""}
							onSelect={handleSelectRater}
							onClear={clearRater}
							error={errorState.rater && !form.raterData}
							errorMessage="Please select a rater"
							allowClear={false}
						/>

						{/* Selected Rater Details */}
						<div className="col-span-1">
							<div className="space-y-2">

								{form.raterData ? (
									<>
										<label className="block text-sm font-semibold text-gray-500">
											Rater Details
										</label>
										<div className="bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-sm">
											<div className="flex items-start space-x-3">
												{/* Details */}
												<div className="flex-1 min-w-0">

													<div className="mb-2">
														<div className="flex items-center space-x-2 mb-1">
															<PersonIcon className="w-4 h-4 text-gray-400" />
															<h3 className="text-sm font-semibold text-gray-500 truncate">
																{form.raterData.id || 'N/A'} - {form.raterData.fullName || 'Unnamed Rater'}
															</h3>
														</div>
													</div>

													{/* Quick Stats Grid */}
													<div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
														<div className="space-y-0.5">
															<span className="text-gray-400">Position</span>
															<p className="font-medium text-gray-700 truncate">
																{form.raterData.position || 'N/A'}
															</p>
														</div>
													</div>

												</div>
											</div>
										</div>
									</>
								) : (
									<div className="bg-gray-50 rounded-lg border-2 border-gray-200 p-6 text-center">
										<div className="mb-3">
											<div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
												<PersonIcon className="text-gray-400" />
											</div>
										</div>
										<p className="text-sm font-medium text-gray-600 mb-1">No Rater Selected</p>
										<p className="text-xs text-gray-400">Search for a rater to continue</p>
									</div>
								)}

							</div>
						</div>

					</div>
				</div>
			</div>

			{/* Appraisal Period Section */}
			<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
				<div className="py-1 border-b border-gray-300">
					<h3 className="font-medium text-gray-700">Appraisal Period</h3>
				</div>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-3 pt-3">

					{/* Appraisal Start Date & Time */}
					<div>
						<label className="block text-sm font-medium text-gray-600 mb-1">
							Appraisal Start: <span className="text-red-500">*</span>
						</label>
						<Input
							type="datetime-local"
							value={appraisalStart}
							onChange={(e) => handleDateTimeChange("appraisalStart", e.target.value)}
							error={errorState.dateStart && !appraisalStart}
							errorMessage="Please select a start date and time"
						/>
						<div className="text-xs text-gray-500 mt-1">
							{appraisalStart ? '' : "Select date and time"}
						</div>
					</div>

					{/* Appraisal End Date & Time */}
					<div>
						<label className="block text-sm font-medium text-gray-600 mb-1">
							Appraisal End: <span className="text-red-500">*</span>
						</label>
						<Input
							type="datetime-local"
							value={appraisalEnd}
							onChange={(e) => handleDateTimeChange("appraisalEnd", e.target.value)}
							error={errorState.dateEnd && !appraisalEnd}
							errorMessage="Please select an end date and time"
						/>
						<div className="text-xs text-gray-500 mt-1">
							{appraisalEnd ? '' : "Select date and time"}
						</div>
					</div>

					{/* Status */}
					<div>
						<StatusSelect
							label="Status:"
							isRequired={true}
							value={form.status}
							onChange={(e) => handleLocalInputChange('status', e.target.value)}
							error={errorState.status && !form.status}
							errorMessage="Please select a status"
						/>
					</div>
				</div>
			</div>

			{/* Summary Section */}
			{form.employeeData && form.raterData && (
				<div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm">
					<div className="py-1 border-b border-gray-300">
						<h3 className="font-medium text-gray-700">Appraisal Summary</h3>
					</div>
					<div className="pb-3 pt-3">
						<div className="bg-blue-50 border border-blue-200 rounded p-4">
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								<div className="text-center">
									<div className="text-xs text-gray-500 font-medium mb-1">EMPLOYEE</div>
									<div className="font-medium text-lg">{form.employeeData.fullName}</div>
									<div className="text-sm text-gray-600 mt-1">{form.employeeData.position}</div>
									<div className="text-xs text-gray-500 mt-2">ID: {form.employeeData.employeeId}</div>
								</div>

								<div className="text-center">
									<div className="text-xs text-gray-500 font-medium mb-1">APPRAISAL PERIOD</div>
									<div className="font-medium text-lg">
										{appraisalStart ? formatDateTime(appraisalStart) : 'Start date/time'}
									</div>
									<div className="text-sm text-gray-600 mt-1">to</div>
									<div className="font-medium text-lg">
										{appraisalEnd ? formatDateTime(appraisalEnd) : 'End date/time'}
									</div>
								</div>

								<div className="text-center">
									<div className="text-xs text-gray-500 font-medium mb-1">RATER</div>
									<div className="font-medium text-lg">{form.raterData.fullName}</div>
									<div className="text-sm text-gray-600 mt-1">{form.raterData.position}</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}